import { MatchableItem } from "../types";
import {
    hasOverAllocatedLines,
    PaymentsByTransaction,
    sumLinePayments,
} from "./multiAllocationHelpers";

export interface AllocationSavePlan<T> {
    entries: T[];
    transactionIdsToRelease: number[];
}

export function planAllocationSaves<T extends { transactionId: number }>({
    document,
    entries,
    paymentsByTransaction,
    persistedPaymentsByTransaction,
}: {
    document: MatchableItem;
    entries: T[];
    paymentsByTransaction: PaymentsByTransaction;
    persistedPaymentsByTransaction: PaymentsByTransaction;
}): AllocationSavePlan<T> {
    const held: PaymentsByTransaction = { ...persistedPaymentsByTransaction };
    const delta = (transactionId: number) =>
        sumLinePayments(paymentsByTransaction[transactionId]) -
        sumLinePayments(persistedPaymentsByTransaction[transactionId]);
    const pendingEntries = [...entries].sort(
        (a, b) => delta(a.transactionId) - delta(b.transactionId),
    );
    const fits = (
        transactionId: number,
        heldPayments: PaymentsByTransaction = held,
    ) =>
        !hasOverAllocatedLines(document, {
            ...heldPayments,
            [transactionId]: paymentsByTransaction[transactionId] ?? {},
        });
    const unblocksAnother = (releasedTransactionId: number) => {
        const heldAfterRelease = { ...held, [releasedTransactionId]: {} };
        return pendingEntries.some((entry) =>
            fits(entry.transactionId, heldAfterRelease),
        );
    };

    const orderedEntries: T[] = [];
    const transactionIdsToRelease: number[] = [];
    while (pendingEntries.length > 0) {
        const fittingIndex = pendingEntries.findIndex((entry) =>
            fits(entry.transactionId),
        );
        if (fittingIndex >= 0) {
            const [entry] = pendingEntries.splice(fittingIndex, 1);
            held[entry.transactionId] =
                paymentsByTransaction[entry.transactionId] ?? {};
            orderedEntries.push(entry);
            continue;
        }
        const releasableEntries = pendingEntries.filter(
            (entry) => sumLinePayments(held[entry.transactionId]) > 0,
        );
        const releasable =
            releasableEntries.find((entry) =>
                unblocksAnother(entry.transactionId),
            ) ?? releasableEntries[0];
        if (!releasable) {
            orderedEntries.push(...pendingEntries);
            break;
        }
        held[releasable.transactionId] = {};
        transactionIdsToRelease.push(releasable.transactionId);
    }
    return { entries: orderedEntries, transactionIdsToRelease };
}
